import Iconfont from 'components/Iconfont'
import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useModal } from 'store/app/hooks'
import { useFastConvertSymbols } from 'store/convert/hooks'
import { CombinedSymbol } from 'utils/symbols'
import { ConvertItem } from './ConvertItem'
import { MarketItem } from './MarketItem'
import {
  FilterInput,
  FilterInputContent,
  Icon,
  Input,
  Label,
  Mask,
  MobileTile,
  Panel,
  SymbolList,
} from './styles'
import { updateModal } from '../../store/app/actions/modalActions'

export function useMarketDrawer() {
  const dispatch = useDispatch()
  const visible = useModal('marketDrawer')

  const open = useCallback(() => {
    dispatch(
      updateModal({
        modal: 'marketDrawer',
        open: true,
      }),
    )
  }, [dispatch])

  const close = useCallback(() => {
    dispatch(
      updateModal({
        modal: 'marketDrawer',
        open: false,
      }),
    )
  }, [dispatch])

  return { visible, open, close }
}

export const MarketDrawer = memo<{ symbols: Array<CombinedSymbol> }>(
  ({ symbols }) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const visibleMarketSelector = useModal('marketDrawer')
    const [searchKey, setSearchKey] = useState('')
    const { close } = useMarketDrawer()
    const fastConvert = useFastConvertSymbols()

    const unionSymbols = useMemo<Array<CombinedSymbol>>(() => {
      if (!searchKey) {
        return symbols
      }
      if (!symbols?.length) {
        return []
      }
      return symbols.filter((item: CombinedSymbol) => {
        return item.name
          .toLocaleLowerCase()
          .includes(searchKey.toLocaleLowerCase())
      })
    }, [symbols, searchKey])

    const filterConvert = useMemo(() => {
      if (!searchKey) {
        return fastConvert
      }
      return fastConvert.filter((item) =>
        `${item.baseName}/${item.quoteName}`
          .toLocaleLowerCase()
          .includes(searchKey.toLocaleLowerCase()),
      )
    }, [searchKey, fastConvert])

    if (!visibleMarketSelector) {
      return null
    }

    return (
      <Mask onClick={close}>
        <Panel onClick={(event: any) => event.stopPropagation()}>
          <MobileTile>
            <div>Markets</div>
            <div
              onClick={() =>
                dispatch(
                  updateModal({
                    modal: 'marketDrawer',
                    open: false,
                  }),
                )
              }
            >
              <Iconfont name="icon-close2" size={20} />
            </div>
          </MobileTile>
          <FilterInput>
            <FilterInputContent>
              <Icon>
                <Iconfont name="icon-Search" size={18} />
              </Icon>
              <Input
                placeholder={t('market-selector-search')}
                onChange={(event) => {
                  setSearchKey(event.target.value)
                }}
              />
            </FilterInputContent>
          </FilterInput>
          <Label>
            <span>{t('market-selector-asset')}</span>
            <span>{t('market-selector-last-price')}</span>
          </Label>
          <SymbolList>
            {unionSymbols?.map((item) => (
              <MarketItem key={item.id} item={item} />
            ))}
            {filterConvert.map((item) => (
              <ConvertItem key={item.tokenId} item={item} />
            ))}
          </SymbolList>
        </Panel>
      </Mask>
    )
  },
)
